import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'

import { skipToken } from '@reduxjs/toolkit/query'

import { useProviderPairMessagingAccess } from './useProviderPairMessagingAccess'
import { getAuthUserId } from '../../../../data/auth/authSelectors'
import { getSelectedClientId } from '../../../../data/lyraTherapy/clientSelectors'
import {
  type EnsureProviderPairConversationInput,
  useEnsureProviderPairConversationMutation,
  useGetProviderPairCareHistoryQuery,
  useGetProviderPairConversationsQuery,
} from '../messagesApi'
import {
  buildProviderPairThread,
  type ProviderPairProfileResolver,
  type ProviderPairThread,
} from '../providerPairUtils'
import { MessengerParticipant, MessengerThread } from '../types'

const isProviderPairThread = (thread: ProviderPairThread | null): thread is ProviderPairThread => thread !== null

export const useProviderPairConversations = () => {
  const providerId = useSelector(getAuthUserId)
  const patientId = useSelector(getSelectedClientId)
  const { canAccessProviderPairs } = useProviderPairMessagingAccess()

  const query = useGetProviderPairConversationsQuery(
    canAccessProviderPairs && providerId && patientId ? { providerId, patientId } : skipToken,
    { refetchOnMountOrArgChange: true, refetchOnFocus: false, refetchOnReconnect: true },
  )
  // A skipped RTK query can retain data from the previous enabled render.
  // Hide it so no selector can expose a pair entry point.
  return {
    ...query,
    data: canAccessProviderPairs ? query.data : undefined,
    currentData: canAccessProviderPairs ? query.currentData : undefined,
  }
}

export const useProviderPairInboxConversations = () => {
  const providerId = useSelector(getAuthUserId)
  const { canAccessProviderPairs } = useProviderPairMessagingAccess()

  const query = useGetProviderPairConversationsQuery(
    canAccessProviderPairs && providerId ? { providerId } : skipToken,
    { refetchOnMountOrArgChange: true, refetchOnFocus: false, refetchOnReconnect: true },
  )
  // A skipped RTK query can retain data from the previous enabled render.
  // Hide it so no selector can expose a pair entry point.
  return {
    ...query,
    data: canAccessProviderPairs ? query.data : undefined,
    currentData: canAccessProviderPairs ? query.currentData : undefined,
  }
}

export const useProviderPairThreads = (resolveProfile: ProviderPairProfileResolver) => {
  const query = useProviderPairConversations()
  // `data` can still belong to the previous client while the query arguments change.
  const threads = useMemo(
    () =>
      query.currentData
        ?.map((conversation) => buildProviderPairThread(conversation, resolveProfile))
        .filter(isProviderPairThread),
    [query.currentData, resolveProfile],
  )

  return { ...query, threads }
}

export const useProviderPairCareHistory = (skip = false) => {
  const providerId = useSelector(getAuthUserId)
  const patientId = useSelector(getSelectedClientId)
  const { canAccessProviderPairs } = useProviderPairMessagingAccess()

  return useGetProviderPairCareHistoryQuery(
    !skip && canAccessProviderPairs && providerId && patientId ? { providerId, patientId } : skipToken,
    {
      refetchOnMountOrArgChange: true,
      refetchOnFocus: false,
      refetchOnReconnect: true,
    },
  )
}

export const useEnsureProviderPairConversation = () => {
  const providerId = useSelector(getAuthUserId)
  const { canAccessProviderPairs } = useProviderPairMessagingAccess()
  const [ensureProviderPairConversation, result] = useEnsureProviderPairConversationMutation()
  const ensure = useCallback(
    (input: EnsureProviderPairConversationInput) => ensureProviderPairConversation({ ...input, providerId }),
    [ensureProviderPairConversation, providerId],
  )

  return [canAccessProviderPairs && providerId ? ensure : undefined, result] as const
}

// A draft pair thread has no conversation until its first send. Keyed by thread id, and a
// resolved id stays memoized until the list catches up, so retrying a failed send reuses the
// conversation. Failures are evicted so the next send tries again. Null on failure; the composer
// surfaces that.
export const useEnsureProviderPairConversationForThread = (thread: MessengerThread | null) => {
  const patientId = useSelector(getSelectedClientId)
  const [ensure] = useEnsureProviderPairConversation()
  const pendingEnsures = useRef(new Map<string, Promise<string | null>>())

  // Once the list shows the conversation, it is the source of truth. If the pair later drops out of
  // the list, the next send ensures again rather than reusing an id that may no longer be valid.
  useEffect(() => {
    if (thread?.conversationId) {
      pendingEnsures.current.delete(thread.id)
    }
  }, [thread?.conversationId, thread?.id])

  return useCallback(async (): Promise<string | null> => {
    if (thread?.conversationId) {
      return thread.conversationId
    }
    if (
      !thread ||
      thread.participant !== MessengerParticipant.PROVIDER_PAIR ||
      !thread.canSendMessages ||
      !ensure ||
      !patientId
    ) {
      return null
    }
    // Pair thread ids already encode the client and both providers.
    const pending = pendingEnsures.current.get(thread.id)
    if (pending) {
      return pending
    }
    const request = ensure({ patientId, targetProviderId: thread.recipientId })
      .unwrap()
      .then(({ external_conversation_id: conversationId }) => conversationId)
      .catch(() => {
        pendingEnsures.current.delete(thread.id)
        return null
      })
    pendingEnsures.current.set(thread.id, request)
    return request
  }, [ensure, patientId, thread])
}
